import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { UtensilsCrossed, Coffee, Soup, Beef, Building2 } from "lucide-react";
import {
  ClickLogo,
  PaymeLogo,
  RahmatLogo,
  YandexEatsLogo,
  EscPosLogo,
  StarPrntLogo,
} from "@/components/logos/PartnerLogos";
import { MARKET, type PartnerLogoKey } from "@/market";

const FORMAT_ICONS = [UtensilsCrossed, Coffee, Soup, Beef, Building2];
const FORMAT_KEYS = ["restaurants", "cafes", "oshkhona", "fastfood", "chains"] as const;

const LOGO_BY_KEY: Record<PartnerLogoKey, typeof ClickLogo> = {
  click: ClickLogo,
  payme: PaymeLogo,
  rahmat: RahmatLogo,
  yandex_eats: YandexEatsLogo,
  esc_pos: EscPosLogo,
  star_prnt: StarPrntLogo,
};

const PARTNER_LOGOS = MARKET.partnerLogos.map(key => LOGO_BY_KEY[key]);

export function Logos() {
  const { t } = useTranslation();

  return (
    <section className="bg-light-muted py-[72px]">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mx-auto max-w-[1200px] px-4 sm:px-6"
      >
        {/* Label */}
        <p className="text-center text-[11px] font-semibold tracking-[0.12em] uppercase text-foreground-subtle">
          {t("logos.title")}
        </p>

        {/* Format icons row */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 sm:gap-x-12 gap-y-5">
          {FORMAT_KEYS.map((key, i) => {
            const Icon = FORMAT_ICONS[i];
            return (
              <div key={key} className="flex items-center gap-2 text-foreground/70">
                <Icon className="w-5 h-5 text-primary shrink-0" aria-hidden="true" strokeWidth={1.75} />
                <span className="text-[14px] font-medium whitespace-nowrap">{t(`logos.formats.${key}`)}</span>
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div className="mt-10 mb-8 border-t border-foreground/10" />

        {/* Real partner logos */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-4 sm:gap-x-6">
          {PARTNER_LOGOS.map((Logo, i) => (
            <Logo
              key={i}
              className="h-12 w-auto object-contain rounded-xl opacity-85 hover:opacity-100 transition-opacity"
            />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
